/**
 * Is this page running inside another app's built-in browser?
 *
 * Friends test #2 (Sep 2026): friends tapped the link inside WhatsApp, played
 * the tutorial and built a village — and next time found nothing. A guest
 * village lives in the browser's storage on that device (D-093), and an
 * app's built-in browser keeps its own storage, often thrown away when the
 * app closes it. Nothing on the server was lost; the device forgot who it was.
 *
 * Detection is by user agent, which is the only signal there is. It catches
 * Android's in-app browsers (they are WebViews and say "; wv)") and the apps
 * that name themselves. iPhone in-app browsers that present as plain Safari
 * cannot be told apart — the note in the invite covers those.
 */
export interface InAppBrowser {
  /** "WhatsApp", "Instagram" … or null when the app does not say. */
  app: string | null;
  android: boolean;
}

const NAMED: readonly (readonly [RegExp, string])[] = [
  [/WhatsApp/iu, "WhatsApp"],
  [/Instagram/iu, "Instagram"],
  [/FBAN|FBAV|FB_IAB|FBIOS/u, "Facebook"],
  [/Telegram/iu, "Telegram"],
  [/\bLine\//u, "LINE"],
  [/Snapchat/iu, "Snapchat"],
  [/TikTok|musical_ly|BytedanceWebview/iu, "TikTok"],
  [/LinkedInApp/iu, "LinkedIn"],
];

export function detectInApp(userAgent: string, isNativeShell = false): InAppBrowser | null {
  // Our own Capacitor app is a WebView too — and its storage is its own and
  // permanent, which is exactly what we want.
  if (isNativeShell) return null;
  const android = /Android/iu.test(userAgent);
  for (const [pattern, app] of NAMED) {
    if (pattern.test(userAgent)) return { app, android };
  }
  if (android && /;\s*wv\)/u.test(userAgent)) return { app: null, android };
  return null;
}

/** An Android link that opens this page in Chrome instead of the app's browser. */
export function chromeIntentUrl(href: string): string {
  const url = new URL(href);
  return `intent://${url.host}${url.pathname}${url.search}#Intent;scheme=${url.protocol.replace(":", "")};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`;
}

/** The running page, checked once. */
export function currentInApp(): InAppBrowser | null {
  if (typeof navigator === "undefined") return null;
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return detectInApp(navigator.userAgent, cap?.isNativePlatform?.() === true);
}
